// LG-Zugriff auf die geraeteinternen Einstellungen (Pro:Centric-/Installer-Menue).
//
// GEMESSEN auf 43UK660H und 43UM767H (09/2026) ueber die API-Aufzaehlung:
//
//   hcap.property  getHotelMode/setHotelMode, getInstallerMenuItem/
//                  setInstallerMenuItem, getPictureMode/setPictureMode,
//                  getPictureProperty/setPictureProperty, getProperty/setProperty
//   hcap.system    getSoundOutput/setSoundOutput, getDefaultSoundOutput/…,
//                  getLocale/getLocaleList/requestLocaleChange,
//                  getNoSignalImage/…, getUsbPowerControl/…, requestCloning,
//                  startManualUpdate, showToastMessage, …
//   hcap.power     getPowerMode/setPowerMode, powerOff, reboot
//   hcap.video     isVideoMute/setVideoMute, getVideoSize/…, OsdTransparency
//   hcap.network   getSoftAP/setSoftAP, ping, …
//
// Daraus folgen drei Dinge:
//
//  1. Es gibt KEIN hcap.audio. Lautstaerke und Stumm sind auf diesen Modellen
//     nicht erreichbar - hcap.video.setVideoMute ist Bild-Stumm (schwarzer
//     Schirm), nicht Ton, und wird deshalb bewusst NICHT auf `mute` gelegt.
//  2. Die Hotelmenue-Werte liegen nicht unter getProperty: jeder geratene
//     Property-Name kam mit "get_tv_property() failed" zurueck. Sie stehen
//     komplett im Dokument von getHotelMode (siehe unten).
//  3. getInstallerMenuItem gibt es auf diesen Geraeten nicht: der Aufruf
//     quittiert mit "Supports only in vptc feature". Auf dem UM767H kam gar
//     keine Antwort mehr - jeder Versuch lief in den Timeout, und die Abfrage
//     dauerte dadurch 8 Sekunden statt 100 Millisekunden. Die Sonde ist
//     deshalb wieder raus; sie hat auf keinem Geraet etwas geliefert, was das
//     Hotel-Dokument nicht schon hat.

import CastLG from '@/helpers/Cast/LG';
import { OSD_LOCALE } from '@/helpers/OsdLanguage';
import { hasHcap, hasHcapMethod, hcapCall, hcapNamespace } from '@/helpers/LgHcap';
import { SETTING_TYPE } from './catalog';

// Kurzes Zeitbudget je Aufruf.
//
// Der Standard des Cast-Wrappers sind 5 s. Bei zwei Dutzend Einstellungen mit
// je zwei Kandidaten wartet eine Firmware, die unbekannte Keys still
// verschluckt, damit minutenlang - und die get_info-Antwort haengt daran. 2 s
// reichen: was es gibt, antwortet in Millisekunden (gemessen: ~5 ms je
// getProperty auf einem UK660H).
const PROPERTY_TIMEOUT_MS = 2000;

// Benannte Felder, in denen der Wert stehen kann. LG antwortet je nach Methode
// mit {soundOutput:0}, {mode:2} oder {specifier:'en-GB'}.
const VALUE_FIELDS = ['value', 'mode', 'level', 'status', 'state', 'enable', 'enabled'];

// Felder, die JEDE Antwort traegt und die nie der gesuchte Wert sind.
//
// Ohne diese Liste lieferte get_sound_output den String "get_sound_output":
// die Antwort ist {command:'get_sound_output', soundOutput:0, result:true}, und
// die Suche nach "dem ersten einfachen Feld" griff das erste - den Befehlsnamen.
// Genau so stand es dann im SocketAdmin.
const META_FIELDS = ['command', 'command_id', 'commandId', 'result', 'errorMessage', 'onSuccess', 'onFailure'];

// Das Feld, in dem der Wert zu einem Getter erwartungsgemaess steht:
// getSoundOutput -> soundOutput. Trifft es nicht, sagt es der Katalog (field).
function derivedField(method) {
  if (!method || method.indexOf('get') !== 0) return '';
  return method.charAt(3).toLowerCase() + method.slice(4);
}

function extractValue(s, preferred) {
  if (s === null || s === undefined) return undefined;
  if (typeof s !== 'object') return s;
  if (preferred) {
    const p = s[preferred];
    if (p !== undefined && typeof p !== 'function' && typeof p !== 'object') return p;
  }
  for (let i = 0; i < VALUE_FIELDS.length; i += 1) {
    const v = s[VALUE_FIELDS[i]];
    if (v !== undefined && typeof v !== 'function' && typeof v !== 'object') return v;
  }
  const keys = Object.keys(s);
  for (let i = 0; i < keys.length; i += 1) {
    if (META_FIELDS.indexOf(keys[i]) !== -1) continue;
    const v = s[keys[i]];
    if (v !== undefined && typeof v !== 'function' && typeof v !== 'object') return v;
  }
  return undefined;
}

/**
 * Der Parametername eines Setters ist nicht dokumentiert.
 *
 * Deshalb gehen mehrere Schreibweisen gleichzeitig mit: `value`, das aus dem
 * Methodennamen abgeleitete Feld (setSoundOutput -> soundOutput) und `mode`.
 * Zusaetzliche Felder ignorieren die hcap-Methoden; welcher gegriffen hat,
 * zeigt ohnehin erst der Rueckvergleich nach dem Setzen.
 */
function setterParams(method, value) {
  const derived = method.indexOf('set') === 0 ? method.charAt(3).toLowerCase() + method.slice(4) : '';
  const params = { value: value, mode: value };
  if (derived) params[derived] = value;
  return params;
}

// Die Property-Kandidaten der Reihe nach durchgehen, beim ersten Treffer Schluss.
function firstProperty(keys, index) {
  const i = index || 0;
  if (i >= keys.length) return Promise.resolve(null);
  return CastLG.getProperty(keys[i], PROPERTY_TIMEOUT_MS)
    .then((value) => {
      if (value === null || value === undefined || value === '') return firstProperty(keys, i + 1);
      return { value: value, via: 'property:' + keys[i], key: keys[i] };
    })
    .catch(() => firstProperty(keys, i + 1));
}

// Die Funktionsnamen eines hcap-Namensraums. Wie in Cast/Tizen.js: manche
// nativen Objekte legen ihre Methoden nur auf dem Prototyp ab, deshalb for..in,
// und Zugriffe, die werfen, werden uebergangen.
function methodNames(api) {
  const out = [];
  if (!api) return out;
  for (const k in api) {
    try {
      if (typeof api[k] === 'function') out.push(k);
    } catch (e) {
      /* manche Properties werfen beim Zugriff - dann eben ohne */
    }
  }
  return out.sort();
}

// --- Hotel-Modus als Dokument ----------------------------------------------
//
// getHotelMode liefert nicht einen Schalter, sondern das KOMPLETTE Hotelmenue
// als JSON-String. Gemessen auf einem 43UK660H:
//
//   {"enable":"on","settings":{
//     "keyManagement":{"enable":"off","settings":{
//        "hidKey":"off","irOperation":"normal","localKeyOperation":"normal"}},
//     "limitedMode":{"enable":"off","settings":{
//        "menuDisplay":"on","osdDisplay":"on","programmeChange":"on", …}},
//     "powerManagement":"off",
//     "powerOnDefault":{"enable":"off","settings":{
//        "aspectRatio":"disable","avSetting":"off","input":"off","programme":"2"}},
//     "powerOnStatus":"stand_by",
//     "volume":{"enable":"off","settings":{
//        "maximumVolume":100,"minimumVolume":0,
//        "startVolume":{"enable":"off","level":0}}}, …}}
//
// Damit ist der ganze LG-Teil geloest: die Hotelmenue-Werte stehen hier, nicht
// in getProperty und nicht im Installer-Menue. Der Katalog adressiert sie ueber
// einen Pfad in dieses Dokument (`lg.hotel`).
//
// Geschrieben wird read-modify-write: das gelesene Dokument wird an EINER
// Stelle geaendert und komplett zurueckgeschickt. Nur das Geaenderte zu senden
// waere ein Versuch am lebenden Objekt - was das Geraet mit einem
// unvollstaendigen Dokument macht, weiss niemand.

let hotelDocPromise = null;

// Schreibvorgaenge auf dem Hotel-Dokument laufen NACHEINANDER.
//
// Der Admin schickt mehrere Aenderungen in einem Kommando, und die Eintraege
// werden nebenlaeufig abgearbeitet. Beim Dokument geht das nicht: jeder
// Schreibvorgang schickt das GANZE Dokument zurueck, zwei gleichzeitige wuerden
// sich also gegenseitig ueberschreiben - die zweite Antwort haette die erste
// Aenderung nie gesehen. Deshalb diese Kette: jeder wartet auf den vorigen und
// liest das Dokument danach frisch.
let hotelWriteChain = Promise.resolve();

function parseHotelDoc(s) {
  const raw = s && (s.hotelMode !== undefined ? s.hotelMode : s.value);
  if (raw === undefined || raw === null) return null;
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(String(raw));
  } catch (e) {
    return null;
  }
}

function readHotelDoc(force) {
  if (!force && hotelDocPromise) return hotelDocPromise;
  hotelDocPromise = hcapCall('property', 'getHotelMode', {})
    .then(parseHotelDoc)
    .catch(() => null);
  return hotelDocPromise;
}

function getPath(obj, path) {
  const parts = String(path).split('.');
  let cur = obj;
  for (let i = 0; i < parts.length; i += 1) {
    if (cur === null || typeof cur !== 'object') return undefined;
    cur = cur[parts[i]];
  }
  return cur;
}

// Legt fehlende Zwischenobjekte NICHT an: ein Pfad, den dieses Modell nicht
// hat, soll ins Leere laufen und nicht ein Feld erfinden, das die Firmware
// hinterher nicht versteht.
function setPath(obj, path, value) {
  const parts = String(path).split('.');
  let cur = obj;
  for (let i = 0; i < parts.length - 1; i += 1) {
    if (cur === null || typeof cur !== 'object' || cur[parts[i]] === undefined) return false;
    cur = cur[parts[i]];
  }
  if (cur === null || typeof cur !== 'object') return false;
  cur[parts[parts.length - 1]] = value;
  return true;
}

// Geraetevokabular -> Draht. `values` im Katalog bildet die Werte des Admins
// auf die des Geraets ab; hier geht es rueckwaerts. Gilt fuer beide Wege, das
// Hotel-Dokument und die eigenen Methoden.
function decodeValue(map, raw) {
  if (!map.values) return raw;
  const keys = Object.keys(map.values);
  for (let i = 0; i < keys.length; i += 1) {
    if (String(map.values[keys[i]]) === String(raw)) return keys[i];
  }
  return raw;
}

// Menuesprache: LG spricht BCP-47 ('de-DE', gemessen ueber getLocale), die App
// und der Admin sprechen Sprachkuerzel ('de'). Die Zuordnung steht schon fuer
// Samsung in OSD_LOCALE ('de' -> 'de_DE.utf8') - dieselbe Tabelle, andere
// Schreibweise, deshalb umgeformt statt ein zweites Mal gepflegt.
function lgLocaleFor(code) {
  const tizen = OSD_LOCALE[String(code || '').toLowerCase()];
  return tizen ? tizen.replace('.utf8', '').replace('_', '-') : '';
}

function codeForLgLocale(specifier) {
  const keys = Object.keys(OSD_LOCALE);
  for (let i = 0; i < keys.length; i += 1) {
    if (lgLocaleFor(keys[i]) === specifier) return keys[i];
  }
  return specifier;
}

// Draht -> Geraetevokabular. Schalter sind im Dokument durchgehend 'on'/'off',
// nicht 1/0 wie bei den Properties.
// Draht -> Geraetevokabular fuer die eigenen Methoden. Anders als im
// Hotel-Dokument gibt es hier keine gemeinsame Schreibweise fuer Schalter,
// deshalb bleibt es bei 1/0, solange der Katalog nichts anderes sagt.
function encodeMethod(entry, m, value) {
  if (m.locale) return lgLocaleFor(value);
  if (m.values) {
    const key = entry.type === SETTING_TYPE.SWITCH ? (value ? 'on' : 'off') : String(value);
    if (m.values[key] !== undefined) return m.values[key];
  }
  if (entry.type === SETTING_TYPE.SWITCH) return value ? 1 : 0;
  if (entry.type === SETTING_TYPE.NUMBER) return Number(value);
  return String(value);
}

function encodeHotel(entry, map, value) {
  if (entry.type === SETTING_TYPE.SWITCH) {
    const word = value ? 'on' : 'off';
    return map.values ? map.values[word] || word : word;
  }
  if (entry.type === SETTING_TYPE.NUMBER) return Number(value);
  const text = String(value);
  return map.values && map.values[text] ? map.values[text] : text;
}

export default class SettingsLG {
  static available() {
    return hasHcap();
  }

  /**
   * Was dieses Geraet an hcap-Namensraeumen und -Methoden mitbringt.
   *
   * Genau diese Aufzaehlung hat auf einem UK660H gezeigt, dass die
   * Hotelmenue-Werte nicht unter getProperty liegen, sondern hinter
   * getInstallerMenuItem - und dass es kein hcap.audio gibt. Sie bleibt in der
   * Antwort, weil sie auf dem naechsten Modell wieder etwas anderes zeigt.
   */
  static apiNames() {
    if (!hasHcap()) return {};
    const out = {};
    ['property', 'system', 'power', 'audio', 'video', 'media', 'network', 'application'].forEach((name) => {
      const api = hcapNamespace(name);
      if (api) out['hcap.' + name] = methodNames(api);
    });
    return out;
  }

  /**
   * Vor jedem Durchlauf aufraeumen.
   *
   * Das Hotel-Dokument wird einmal gelesen und von allen Eintraegen benutzt -
   * sonst ginge derselbe getHotelMode ein Dutzend Mal ans Geraet. Zwischen zwei
   * Abfragen muss der Zwischenspeicher aber weg, sonst zeigt "Geraet abfragen"
   * beim zweiten Mal den Stand vom ersten.
   */
  static begin() {
    hotelDocPromise = null;
  }

  // Liefert Promise<{ value, via, key }> oder Promise<null>.
  static read(entry) {
    const map = entry.lg;
    if (!map || !hasHcap()) return Promise.resolve(null);

    // 1. Das Hotel-Dokument - dort stehen die Hotelmenue-Werte.
    if (map.hotel) {
      return readHotelDoc().then((doc) => {
        if (!doc) return null;
        const raw = getPath(doc, map.hotel);
        if (raw === undefined || raw === null || raw === '') return null;
        return { value: decodeValue(map, raw), via: 'hotelMode:' + map.hotel };
      });
    }

    // 2. Eine eigene Methode, wenn der Katalog eine gemessene kennt.
    const m = map.method;
    const viaMethod =
      m && hasHcapMethod(m.ns, m.get)
        ? hcapCall(m.ns, m.get, m.params || {})
            .then((s) => {
              const via = 'hcap.' + m.ns + '.' + m.get;
              const raw = extractValue(s, m.field || derivedField(m.get));
              if (raw === undefined) return { error: 'Antwort ohne Wert', via: via };
              const v = m.locale ? codeForLgLocale(String(raw)) : decodeValue(m, raw);
              return { value: v, via: via };
            })
            // Der Grund gehoert an die Zeile: "Methode gibt es, Geraet
            // verweigert" ist eine andere Aussage als "kennt diese Firmware
            // nicht".
            .catch((f) => ({
              error: String((f && (f.errorMessage || f.message)) || f),
              via: 'hcap.' + m.ns + '.' + m.get,
            }))
        : Promise.resolve(null);

    // 3. Sonst die Property-Kandidaten.
    return viaMethod.then((hit) => {
      if (hit && hit.value !== undefined) return hit;
      if (!map.property || !map.property.length) return hit || null;
      return firstProperty(map.property, 0).then((found) => found || hit || null);
    });
  }

  /**
   * Setzt einen Wert und liest ihn zurueck.
   *
   * Geschrieben wird nur ueber den Weg, der auch gelesen hat. Ein setProperty
   * auf einen Namen, den die Firmware nicht kennt, quittiert je nach Modell mit
   * Erfolg - eine Rueckmeldung "gesetzt", die nichts gesetzt hat, waere
   * schlimmer als ein ehrliches "kennt diese Firmware nicht".
   */
  static write(entry, value) {
    const map = entry.lg;
    if (!map) return Promise.resolve({ ok: false, detail: 'auf LG nicht verfuegbar' });
    if (!hasHcap()) return Promise.resolve({ ok: false, detail: 'hcap fehlt' });

    // Hotel-Dokument: einmal lesen, an einer Stelle aendern, komplett zurueck.
    if (map.hotel) {
      const run = () =>
        readHotelDoc(true).then((doc) => {
          if (!doc) return { ok: false, detail: 'Hotel-Modus nicht lesbar - nichts gesetzt' };
          const copy = JSON.parse(JSON.stringify(doc));
          if (!setPath(copy, map.hotel, encodeHotel(entry, map, value)))
            return { ok: false, detail: 'dieses Modell kennt "' + map.hotel + '" nicht' };
          // Die Bloecke des Hotelmenues haben einen eigenen Schalter: eine
          // Maximallautstaerke bei volume.enable = off aendert nichts. Wer den
          // Wert setzt, will ihn wirksam haben.
          if (map.enablePath) setPath(copy, map.enablePath, 'on');
          return hcapCall('property', 'setHotelMode', { hotelMode: JSON.stringify(copy) })
            .then(() => readHotelDoc(true))
            .then((fresh) => {
              const raw = fresh ? getPath(fresh, map.hotel) : undefined;
              return {
                ok: true,
                value: raw === undefined ? undefined : decodeValue(map, raw),
                via: 'hotelMode:' + map.hotel,
              };
            })
            .catch((f) => ({
              ok: false,
              via: 'hotelMode:' + map.hotel,
              detail: String((f && (f.errorMessage || f.message)) || f),
            }));
        });

      // In die Kette einreihen - und sie nicht abreissen lassen, wenn einer
      // scheitert.
      const queued = hotelWriteChain.then(run, run);
      hotelWriteChain = queued.then(() => undefined, () => undefined);
      return queued;
    }

    return SettingsLG.read(entry).then((current) => {
      // Auch ein Lesefehler heisst: nicht schreiben. Blind zu setzen, was sich
      // nicht zurueckpruefen laesst, waere auf einem Geraet im Gastzimmer der
      // falsche Mut - und der Grund gehoert in die Absage.
      if (!current || current.value === undefined)
        return {
          ok: false,
          via: current && current.via,
          detail:
            'diese Firmware meldet die Einstellung nicht - nichts gesetzt' +
            (current && current.error ? ' (' + current.error + ')' : ''),
        };

      let native = value;
      if (entry.type === SETTING_TYPE.SWITCH) native = value ? 1 : 0;
      else if (entry.type === SETTING_TYPE.NUMBER) native = Number(value);

      const m = map.method;
      if (m && current.via === 'hcap.' + m.ns + '.' + m.get) {
        if (!m.set)
          return { ok: false, via: current.via, detail: 'nur lesbar - diese Firmware bietet keinen Setter' };
        native = encodeMethod(entry, m, value);
        if (native === '' || native === undefined)
          return { ok: false, via: current.via, detail: 'kein Geraetewert fuer "' + value + '" bekannt' };
        const params = Object.assign({}, m.params, setterParams(m.set, native));
        // Kennt der Katalog den Parameternamen (gemessen oder dokumentiert),
        // hat er Vorrang vor den abgeleiteten Schreibweisen.
        if (m.setParam) params[m.setParam] = native;
        return hcapCall(m.ns, m.set, params)
          .then(() => SettingsLG.read(entry))
          .then((after) => ({
            ok: true,
            value: after ? after.value : undefined,
            via: 'hcap.' + m.ns + '.' + m.set,
          }))
          .catch((f) => ({
            ok: false,
            via: 'hcap.' + m.ns + '.' + m.set,
            detail: String((f && (f.errorMessage || f.message)) || f),
          }));
      }

      if (!current.key)
        return { ok: false, via: current.via, detail: 'kein Schreibweg zu diesem Lesewert' };

      return CastLG.setProperty(current.key, native, PROPERTY_TIMEOUT_MS)
        .then(() => SettingsLG.read(entry))
        .then((after) => ({ ok: true, value: after ? after.value : undefined, via: current.via }))
        .catch((f) => ({
          ok: false,
          detail: String((f && (f.errorMessage || f.message)) || f),
          via: current.via,
        }));
    });
  }
}



// WEBPACK FOOTER //
// ./src/helpers/DeviceSettings/LG.js